<template>
  <div class="col-12">
    <div class="pl-2">
      <section-title content="Búsqueda de Especialista" :has-line="true" />
      <div v-if="showSupervised">
        <specialist-for-supervise-result-row
          v-for="(row, idx) of supervisedResults"
          :key="`result-${idx}`"
          class="mb-3"
          :data="row"
          :show-action="false"
          @on-select-specialist="onItemSelectedHandler(row)"
        />
        <pagination
          v-if="
            supervisedPagination.totalItemCount > supervisedPagination.pageSize
          "
          :data="supervisedPagination"
          show-text
          @pagination-go-page="goToSupervisedPageHandler"
        />
        <div class="d-flex flex-row-reverse mt-3">
          <action-button
            id="btn-supervised-add"
            i="fas fa-file-medical fa-lg"
            text="Añadir"
            variant="success"
            @click="showFind"
          />
        </div>
      </div>
      <div v-if="!showSupervised">
        <custom-separator
          v-if="addedSpecialists.length"
          class="my-4"
          text="Selección de Especialistas"
        />
        <custom-chips-group
          v-if="addedSpecialists.length"
          class="text-left"
          no-background
          no-wrap
          object-text="name"
          :on-item-deleted="onItemDeletedHandler"
          :selected-items="addedSpecialists"
          title=""
        />
        <filter-manager
          ref="filterRef"
          v-model="filterModel"
          class="mb-2"
          :display-advanced="false"
          :filters="filtersConfig"
          search-container-class="d-flex justify-content-end align-items-end"
          @search="onSearchHandler"
          @specialtyType="onSpecialtyTypeChange"
        />
        <div v-if="results.length != 0">
          <custom-separator class="my-4" text="Resultado de Especialistas" />
          <specialist-for-supervise-result-row
            v-for="(row, idx) of results"
            :key="`result-${idx}`"
            class="mb-3"
            :data="row"
            @on-select-specialist="onItemSelectedHandler(row)"
          />
          <pagination
            v-if="pagination.totalItemCount > pagination.pageSize"
            :data="pagination"
            show-text
            @pagination-go-page="goToPageHandler"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import objectToFormData from 'object-to-formdata';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import SectionTitle from 'SectionTitle';
import FilterManager from 'FilterManager';
import Pagination from 'pagination';
import CustomSeparator from 'custom-separator';
import CustomChipsGroup from 'CustomChipsGroup';
import ActionButton from 'ActionButton';
import SpecialistForSuperviseResultRow from './SpecialistForSuperviseResultRow.vue';
import {
  getSpecialistsBySpecialtyType as _getSpecialistsBySpecialtyType,
  searchSpecialistsForSupervise as _searchSpecialistsForSupervise,
  searchSupervisedSpecialists as _searchSupervisedSpecialists,
  saveSupervisedSpecialists as _saveSupervisedSpecialists,
} from '@/services/provider/specialist.api.js';

const DEFAULT_PAGE_SIZE = 20;

export default {
  name: 'SpecialistSupervision',
  components: {
    SectionTitle,
    FilterManager,
    Pagination,
    CustomSeparator,
    CustomChipsGroup,
    ActionButton,
    SpecialistForSuperviseResultRow,
  },
  mixins: [alertMixin],
  props: {
    academicYearId: {
      type: Number,
      default: () => null,
    },
    companyId: {
      type: Number,
      default: () => null,
    },
    superviserSpecialistId: {
      type: Number,
      default: () => null,
    },
    filterOptions: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    hasSupervised: false,
    filterModel: {
      specialtyType: null,
      specialist: null,
    },
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    results: [],
    supervisedPagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    supervisedResults: [],
    addedSpecialists: [],
  }),
  computed: {
    filtersConfig() {
      return [
        {
          name: 'specialtyType',
          label: 'Especialidad:',
          component: 'CustomDropDown',
          value: this.filterModel.specialtyType,
          options: this.filterOptions.specialties,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          clearable: true,
          validations: 'required',
          error: 'La especialidad es requerida.',
        },
        {
          name: 'specialist',
          label: 'Nombre:',
          component: 'CustomDropDown',
          value: this.filterModel.specialist,
          options: this.filterOptions.specialists,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-6',
          clearable: true,
          disabled: !this.filterModel.specialtyType?.id,
        },
      ];
    },
    showSupervised() {
      return this.hasSupervised;
    },
    selectedSpecilists() {
      const specialists = this.addedSpecialists?.map((x) => x.id);
      return [...new Set(specialists)];
    },
  },
  async mounted() {
    await this.onLoad();
  },
  methods: {
    async onLoad() {
      await Promise.all([this.onSearchSupervisedHandler()]);

      if (this.supervisedResults.length > 0) {
        this.hasSupervised = true;
        this.supervisedResults.forEach((item) => {
          this.onAddSpecialist(item);
        });
      }
    },
    showFind() {
      this.hasSupervised = false;
      this.filterModel = {
        specialtyType: null,
        specialist: null,
      };
      this.pagination = {
        currentPage: 1,
        totalItemCount: 1,
        pageCount: 1,
        count: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      };
      this.results = [];
    },
    async onSpecialtyTypeChange(value) {
      if (value?.id) {
        this.filterOptions.specialists = (
          await _getSpecialistsBySpecialtyType(
            value.id,
            this.superviserSpecialistId,
            this.companyId,
            this.academicYearId
          )
        ).data;
      }
    },
    async onSearchSupervisedHandler() {
      this.supervisedPagination.currentPage = 1;
      await this.searchSupervisedSpecialists();
    },
    async searchSupervisedSpecialists() {
      const payload = {
        academicYearId: this.academicYearId,
        companyId: this.companyId,
        superviserSpecialistId: this.superviserSpecialistId,
        pageIndex: this.supervisedPagination.currentPage,
        pageSize: this.supervisedPagination.pageSize,
      };

      const res = (await _searchSupervisedSpecialists(payload)).data;
      this.supervisedResults = res.data;
      this.supervisedPagination = {
        ...this.supervisedPagination,
        count: res.count,
        currentPage: res.currentPage,
        pageCount: res.pageCount,
        totalItemCount: res.totalItemCount,
      };
    },
    async goToSupervisedPageHandler(page) {
      this.supervisedPagination.currentPage = page;
      this.searchSupervisedSpecialists();
    },
    async onSearchHandler() {
      const isValid = await this.$refs.filterRef.$refs.formValidator.validate();

      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }

      this.pagination.currentPage = 1;
      this.searchSpecialists();
    },
    async searchSpecialists() {
      const payload = this.fillPayload();
      const res = (await _searchSpecialistsForSupervise(payload)).data;
      this.results = res.data;
      this.pagination = {
        ...this.pagination,
        count: res.count,
        currentPage: res.currentPage,
        pageCount: res.pageCount,
        totalItemCount: res.totalItemCount,
      };
    },
    async goToPageHandler(page) {
      this.pagination.currentPage = page;
      this.searchSpecialists();
    },
    onItemSelectedHandler(item) {
      if (!this.addedSpecialists.find((x) => x.id === item.specialistId)) {
        this.onAddSpecialist(item);

        this.pagination.currentPage = 1;
        this.searchSpecialists();
      }
    },
    onAddSpecialist(item) {
      this.addedSpecialists.push({
        id: item.specialistId,
        name: item.fullName + item.ssn.replace('*****', '-'),
        highlight: true,
      });
    },
    onItemDeletedHandler(item) {
      this.addedSpecialists.splice(this.addedSpecialists.indexOf(item), 1);

      this.pagination.currentPage = 1;
      this.searchSpecialists();
    },
    fillPayload() {
      const payload = {
        specialtyTypeId: this.filterModel.specialtyType.id,
        superviserSpecialistId: this.superviserSpecialistId,
        supervisedSpecialistId: this.filterModel.specialist?.id,
        selectedSpecialists: this.selectedSpecilists,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };

      return payload;
    },
    async onSaveSupervisedSpecialists() {
      if (this.selectedSpecilists.length > 0) {
        const payload = {
          academicYearId: this.academicYearId,
          companyId: this.companyId,
          superviserSpecialistId: this.superviserSpecialistId,
          supervisedSpecialistIds: this.selectedSpecilists,
        };

        const params = objectToFormData(
          { ...payload },
          { indices: true, nullsAsUndefineds: true }
        );

        await _saveSupervisedSpecialists(params).catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
          return;
        });

        this.hasSupervised = true;
        this.onSearchSupervisedHandler();

        this.ShowToast(
          'Proceso Exitoso',
          'El(los) especialista(s) fueron guardados exitosamente.',
          'success'
        );
      }
    },
  },
};
</script>
<style scoped>
.filter-manager {
  background-color: white;
}
</style>
